import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import SideNavMenuItem from './SideNavMenuItems';
import ToggleArrowIcon from '../../assets/Icons/components/ToggleArrowIcon';
import ContractIcon from '../../assets/Icons/components/ContractIcon';
import DashboardIcon from '../../assets/Icons/components/DashboardIcon';
import UserProfile from './UserProfile';
import { getAuthData } from '../../utils/auth';
import LogoutConfirmation from './LogoutPopup'; // Import the new component
import Popup from '../../components/Popup/Popup';
import { authService } from '@/services/authService/authServiceEndpoints';
import { clearDistrict } from '@/reducers/admin/districtIdSlice';
import { useDispatch } from 'react-redux';
import { resetUser, trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';
import { tokenUrl } from '@/common/config';

interface IMenuItem {
  icon: React.ReactNode;
  label: string;
  path: string;
}

export const adminMenuItems: IMenuItem[] = [
  { icon: <DashboardIcon />, label: 'Dashboard', path: '/dashboard' },
  { icon: <ContractIcon />, label: 'Plaintiff Sheets', path: '/plaintiffSheets' },
];

interface ISidebarMenuProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  user?: { role?: string } | null;
}

const SideNav: React.FC<ISidebarMenuProps> = ({ isCollapsed, toggleSidebar }) => {
  const { username, email } = getAuthData();
  const navigate = useNavigate();
  const location = useLocation(); // Added missing useLocation hook
  const [showLogoutPopup, setShowLogoutPopup] = useState(false); // Add state for logout popup
  const dispatch = useDispatch();

  const menuItems = adminMenuItems;

  const [activeItem, setActiveItem] = useState<string>();

  // Sync active item based on current URL with exact matching logic
  useEffect(() => {
    // Find the best matching item using more precise logic
    let matchedItem: IMenuItem | undefined;

    // First, try to find an exact match
    matchedItem = menuItems.find((item) => location.pathname === item.path);

    // If no exact match, find the longest matching path that starts with the item path
    if (!matchedItem) {
      matchedItem = menuItems
        .filter((item) => location.pathname.startsWith(item.path))
        .sort((a, b) => b.path.length - a.path.length)[0];
    }

    if (matchedItem) {
      setActiveItem(matchedItem.label);
    } else {
      // Clear active item if no match found
      setActiveItem(undefined);
    }
  }, [location.pathname, menuItems]); // Added proper dependencies

  const handleMenuItemClick = (item: IMenuItem) => {
    dispatch(clearDistrict());
    setActiveItem(item.label);
    navigate(item.path);
  };

  // Open the logout confirmation popup
  const openLogoutPopup = () => {
    setShowLogoutPopup(true);
  };

  // Close the logout confirmation popup
  const closeLogoutPopup = () => {
    setShowLogoutPopup(false);
  };

  const handleLogout = async () => {
    try {
      trackEvent(PostHogEvents.USER_LOGGED_OUT, {
        user_id: email,
        timestamp: new Date().toISOString(),
        message: PostHogMessages[PostHogEvents.USER_LOGGED_OUT],
      });

      // Clear PostHog identity
      resetUser();

      // Perform logout actions that must happen regardless of authService result
      const performLogoutCleanup = () => {
        localStorage.removeItem(tokenUrl as string);
        sessionStorage.clear();
        window.location.href = '/admin/login';
        navigate('/admin/login');
      };

      try {
        // Attempt server logout
        await authService.signOut();
      } catch (error) {
        console.error('Logout API failed, proceeding with client-side cleanup:', error);
        // Continue with cleanup even if API fails
      } finally {
        // Always perform cleanup
        performLogoutCleanup();
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback cleanup if something fails in the main try block
      localStorage.removeItem(tokenUrl as string);
      sessionStorage.clear();
      window.location.href = '/admin/login';
      navigate('/admin/login');
    }
  };

  return (
    <div className=" h-full">
      {/* Render logout confirmation popup conditionally */}

      <nav
        className={`flex flex-col h-full  transition-all duration-300 ease-in-out rounded-lg ${isCollapsed ? 'w-20 p-2' : 'w-56 p-2'}`}
      >
        <section className="flex flex-col my-2   flex-1 w-full">
          {menuItems.map((item) => (
            <SideNavMenuItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              isActive={activeItem === item.label}
              isCollapsed={isCollapsed}
              onClick={() => handleMenuItemClick(item)}
              textClassName={activeItem === item.label ? 'font-semibold' : 'font-normal'}
            />
          ))}
        </section>

        <div className="flex flex-col gap-3 my-4">
          <UserProfile
            isCollapsed={isCollapsed}
            username={username}
            isAuthenticated={true}
            role={'Admin'}
            handleLogout={openLogoutPopup} // Changed to open the popup instead of direct logout
          />
          <Tooltip
            data-testid="toggle-arrow-container"
            title={isCollapsed ? 'Expand' : ''}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#4F4F4F',
                  color: '#FFFFFF',
                  fontSize: '11px',
                  fontWeight: 600,
                  py: '4px',
                  px: '8px',
                  borderRadius: '2px 0px 0px 0px',
                  ['& .MuiTooltip-arrow']: { color: '#4F4F4F' },
                },
              },
            }}
          >
            <button
              onClick={toggleSidebar}
              className="flex-shrink-0 focus:outline-none w-full px-3"
              data-testid="toggle-arrow-container"
            >
              <div
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-start'} transition-all duration-300`}
              >
                <div
                  className={`transition-transform duration-800 ease-in-out ${isCollapsed ? 'rotate-180' : 'rotate-0'}`}
                >
                  <ToggleArrowIcon />
                </div>
                {!isCollapsed && (
                  <span className="font-medium text-gray-550 font-inter text-xs leading-[14.4px] ml-[17px]">
                    Collapse
                  </span>
                )}
              </div>
            </button>
          </Tooltip>
          <div className="z-50">
            {showLogoutPopup && (
              <Popup
                isOpen={showLogoutPopup}
                title="Logout Confirmation"
                onClose={closeLogoutPopup}
              >
                <LogoutConfirmation closePopUp={closeLogoutPopup} handleLogout={handleLogout} />
              </Popup>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default SideNav;
